import React, {useEffect, useState} from "react";
import {connect, useSelector} from "react-redux";
import {getQuotes} from "../../actions/quote";
import {getClassNamesForThButton, useSortableData, alphabetizeByAttributeReverse, formatDateTimeToZone} from "../common/Helpers";
import DocStatusTag from "../common/DocStatusTag";
import QuoteCreateModal from "./QuoteCreateModal";

// Styled to match the Invoice Management page (ManagementDrops.js's controls bar +
// InvoiceList.js's table), minus the Project/Contract/Invoice drop-grid selector -- a Quote is
// project-less and cold-sendable, so there's nothing for that selector to scope by.
export function QuoteList(props) {

    const quotes = useSelector((state) => state.quote.quotes);
    const noQuotesExist = useSelector((state) => state.quote.noQuotesExist);

    const [modalOpen, setModalOpen] = useState(false);
    const [render, setRender] = useState(noQuotesExist || quotes.length > 0);

    useEffect(() => {
        props.getQuotes();
    }, []);

    // Start rendering the component only once the list data is returned (matches
    // InvoiceList.js/WorkflowList.js's pattern, avoiding a flash of the empty state)
    useEffect(() => {
        if (!render) {
            (noQuotesExist || quotes.length > 0) && setRender(true);
        }
    }, [noQuotesExist, quotes]);

    const orderedQuotes = alphabetizeByAttributeReverse(quotes, "date_created");
    const {items, requestSort, sortConfig} = useSortableData(orderedQuotes);

    const received = items.filter((quote) => quote.is_recipient_company);
    const sent = items.filter((quote) => quote.is_sender_company);

    const handleSelectQuote = (uuid) => {
        props.history.push(`/quote/view/${uuid}`);
    };

    const counterpartyLabel = (quote) => quote.is_recipient_company ? quote.fk_company_initiated_by.name : (quote.fk_email_recipient || "-");

    const renderRow = (quote) => (
        <tr key={quote.quote_id} className="table-row" onClick={() => handleSelectQuote(quote.quote_id)}>
            <td className="typical flexi content1 multiline light-black name first" style={{width: "28%"}}>
                <span className="heavy">{quote.quote_name || "Untitled Quote"}</span>
            </td>
            <td className="typical flexi content1 light-black" style={{width: "24%"}}>
                {counterpartyLabel(quote)}
            </td>
            <td className="typical flexi content1 light-black" style={{width: "20%"}}>
                {quote.pending_date ? formatDateTimeToZone(quote.pending_date) : "-"}
            </td>
            <td className="typical flexi content1 light-black" style={{width: "14%"}}>
                {quote.quote_value ? `$${quote.quote_value}` : "-"}
            </td>
            <td className="typical last-cell rigid content1 light-black">
                <DocStatusTag status={quote.contract_status === "Converted" ? "Accepted" : quote.contract_status} fontSize="content3" />
            </td>
        </tr>
    );

    const noResults =
        <div className="page-card narrow light-black g5-text">You have no quotes yet.</div>

    const quoteTable =
        <table className="card-table">
            <thead>
                <tr>
                    <th className="content2 heavy-grey flexi first" style={{width: "28%"}}>
                        <button type="button" onClick={() => requestSort("quote_name")} className={getClassNamesForThButton("quote_name", sortConfig)}>
                            Quote Name
                        </button>
                    </th>
                    <th className="content2 heavy-grey flexi" style={{width: "24%"}}>
                        Counterparty
                    </th>
                    <th className="content2 heavy-grey flexi" style={{width: "20%"}}>
                        <button type="button" onClick={() => requestSort("pending_date")} className={getClassNamesForThButton("pending_date", sortConfig)}>
                            Sent
                        </button>
                    </th>
                    <th className="content2 heavy-grey flexi" style={{width: "14%"}}>
                        <button type="button" onClick={() => requestSort("quote_value")} className={getClassNamesForThButton("quote_value", sortConfig)}>
                            Value
                        </button>
                    </th>
                    <th className="content2 heavy-grey rigid last-cell">
                        <button type="button" onClick={() => requestSort("contract_status")} className={getClassNamesForThButton("contract_status", sortConfig)}>
                            Status
                        </button>
                    </th>
                </tr>
            </thead>
            <tbody id="section1">
                {received.length > 0 && <tr><th className="content3 p4-text p8b action-header" colSpan="5">RECEIVED</th></tr>}
                {received.map(renderRow)}
            </tbody>
            <tbody id="section2">
                {sent.length > 0 && <tr><th className="content3 g3-text g8 action-header" colSpan="5">SENT</th></tr>}
                {sent.map(renderRow)}
            </tbody>
        </table>

    return (
        <div className="page-wrapper">
            {render &&
                <>
                    <div className="mng-controls-wrapper gap-below-xs gap-above-s">
                        <button id="create_quote" className="btn pri min-width" onClick={() => setModalOpen(true)}>New Quote</button>
                    </div>

                    <div className="table-wrapper">
                        {items.length > 0 ? quoteTable : noResults}
                    </div>

                    {modalOpen && <QuoteCreateModal setModalOpen={setModalOpen} history={props.history} />}
                </>
            }
        </div>
    );
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps, {getQuotes})(QuoteList);
